import React, { useRef, useEffect } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { Utterance, TranscriptionStatus } from '../../../types/annotation';
import UtteranceBlock from '../utterance/UtteranceBlock';

interface TranscriptPanelProps {
  utterances: Utterance[];
  selectedUtteranceId: string | null;
  isReviewMode: boolean;
  transcriptionStatus?: TranscriptionStatus;
  onSelectUtterance: (id: string) => void;
  onUpdateUtterance: (id: string, updates: Partial<Utterance>) => void;
  onDeleteUtterance: (id: string) => void;
  onPlaySegment: (start: number, end: number) => void;
  onAddSegment: (start?: number, end?: number) => void;
  onSplitUtterance: (utteranceId: string, splitTime: number) => void;
  currentTime: number;
  isPlaying: boolean;
}

const TranscriptPanel: React.FC<TranscriptPanelProps> = ({
  utterances,
  selectedUtteranceId,
  isReviewMode,
  transcriptionStatus,
  onSelectUtterance,
  onUpdateUtterance,
  onDeleteUtterance,
  onPlaySegment,
  onAddSegment,
  onSplitUtterance,
  currentTime,
  isPlaying,
}) => {
  const sorted = [...utterances].sort((a, b) => a.sequenceIndex - b.sequenceIndex);
  const selectedRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to selected utterance when it changes
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedUtteranceId]);

  // Determine which utterance is "active" by current playback time
  const activeByTime = sorted.find(
    (u) => currentTime >= u.startTime && currentTime <= u.endTime,
  );

  if (sorted.length === 0) {
    const isTranscribing = transcriptionStatus === 'queued' || transcriptionStatus === 'running';

    return (
      <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center">
          {isTranscribing ? (
            <>
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
              <div>
                <p className="text-sm font-medium text-gray-700">Transcription in progress</p>
                <p className="text-xs text-gray-400 mt-0.5 max-w-xs">
                  Whisper is transcribing the audio in Hindi. Segments will appear here when
                  complete. This usually takes 5–15 minutes.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <Plus className="w-6 h-6 text-gray-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">No segments yet</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Drag on the waveform or click the button below to add a segment.
                </p>
              </div>
              {!isReviewMode && (
                <button
                  type="button"
                  onClick={() => onAddSegment()}
                  className="px-4 py-2 text-sm rounded-lg font-medium bg-violet-600 text-white hover:bg-violet-700 transition-colors"
                >
                  + Add Segment
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
        <h3 className="text-sm font-semibold text-gray-700">
          Transcript
          <span className="ml-2 text-xs font-normal text-gray-400">
            {sorted.length} segment{sorted.length !== 1 ? 's' : ''}
          </span>
        </h3>
      </div>

      {/* Scrollable utterance list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {sorted.map((utterance) => {
          const isSelected = utterance.utteranceId === selectedUtteranceId;
          const isActiveByTime =
            !isSelected && utterance.utteranceId === activeByTime?.utteranceId;

          return (
            <div
              key={utterance.utteranceId}
              ref={isSelected ? selectedRef : undefined}
              className={[
                'transition-all rounded-xl',
                isActiveByTime ? 'ring-1 ring-blue-300 border-l-4 border-l-blue-400' : '',
              ].join(' ')}
            >
              <UtteranceBlock
                utterance={utterance}
                isSelected={isSelected}
                isReviewMode={isReviewMode}
                onSelect={() => onSelectUtterance(utterance.utteranceId)}
                onUpdate={(updates) => onUpdateUtterance(utterance.utteranceId, updates)}
                onDelete={() => onDeleteUtterance(utterance.utteranceId)}
                onPlaySegment={onPlaySegment}
                onSplit={onSplitUtterance}
                currentTime={currentTime}
                isPlaying={isPlaying}
              />
            </div>
          );
        })}

        {/* Add segment at bottom */}
        {!isReviewMode && (
          <button
            type="button"
            onClick={() => onAddSegment()}
            className="w-full py-2 text-sm rounded-xl border border-dashed border-violet-300 text-violet-600 hover:bg-violet-50 hover:border-violet-400 transition-colors font-medium"
          >
            + Add Segment
          </button>
        )}
      </div>
    </div>
  );
};

export default TranscriptPanel;
